import React from 'react';
import { Grid, Box, TextField, FormControlLabel, Switch, Button, useMediaQuery } from '@mui/material';

export default function SourdoughInput({
  flour,
  protein,
  starter,
  starterHydration,
  doughHydration,
  temperature,
  isFahrenheit,
  notes,
  setField,
  onToggleTemperature,
  handleKeyDown
}) {
  // Check if the user is on a mobile device
  const isMobile = useMediaQuery('(max-width:600px)');

  // Custom keyDown handler for notes so SHIFT+ENTER => newline, ENTER alone => submit
  const handleNotesKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleKeyDown(e);
    }
  };

  // Helper to prevent API call on Enter, but still allow initial calculations
  const handleKeyDownNoApi = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // So the parent knows to skip the API call
    }
    handleKeyDown(e);
  };

  // Determine if all required fields are valid
  const isValid =
    flour > 0 &&
    protein >= 5 && protein <= 20 &&
    starter >= 10 && starter <= 1000 &&
    starterHydration >= 50 && starterHydration <= 150 &&
    doughHydration >= 40 && doughHydration <= 110; 
  // (No restriction placed on temperature or notes here)

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {/* Two Columns */}
      <Grid item xs={12} md={6}>
        <TextField
          label="Flour (g)"
          fullWidth
          value={flour}
          onChange={(e) => setField('flour', e.target.value)}
          // onKeyDown={handleKeyDownNoApi}
          color="primary"
          error={flour <= 0}
          helperText={flour <= 0 ? "Flour must be greater than 0." : ""}
          sx={{
            borderRadius: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#fdfdf7',
              height: isMobile ? '40px' : 'auto'
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Protein Content (%)"
          fullWidth
          value={protein}
          onChange={(e) => setField('protein', e.target.value)}
          //onKeyDown={handleKeyDownNoApi}
          color="primary"
          error={protein < 5 || protein > 20}
          helperText={protein < 5 || protein > 20 ? "Must be between 5-20%." : ""}
          sx={{
            borderRadius: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#fdfdf7',
              height: isMobile ? '40px' : 'auto'
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Starter (g)"
          fullWidth
          value={starter}
          onChange={(e) => setField('starter', e.target.value)}
          color="primary"
          error={starter < 10 || starter > 1000}
          helperText={starter < 10 || starter > 1000 ? "Must be between 10-1000g." : ""}
          sx={{
            borderRadius: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#fdfdf7',
              height: isMobile ? '40px' : 'auto'
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Starter Hydration (%)"
          fullWidth
          value={starterHydration}
          onChange={(e) => setField('starterHydration', e.target.value)}
          //onKeyDown={handleKeyDownNoApi}
          color="primary"
          error={starterHydration < 50 || starterHydration > 150}
          helperText={starterHydration < 50 || starterHydration > 150 ? "Must be between 50-150%." : ""}
          sx={{
            borderRadius: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#fdfdf7',
              height: isMobile ? '40px' : 'auto'
            }
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Dough Hydration (%)"
          fullWidth
          value={doughHydration}
          onChange={(e) => setField('doughHydration', e.target.value)}
          //onKeyDown={handleKeyDownNoApi}
          color="primary"
          error={doughHydration < 40 || doughHydration > 110}
          helperText={doughHydration < 40 || doughHydration > 110 ? "Must be between 40-110%." : ""}
          sx={{
            borderRadius: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#fdfdf7',
              height: isMobile ? '40px' : 'auto'
            }
          }}
        />
      </Grid>

      {/* Temperature & Switch in One Row */}
      <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          label={`Room Temp (${isFahrenheit ? '°F' : '°C'})`}
          value={temperature}
          onChange={(e) => setField('temperature', e.target.value)}
          //onKeyDown={handleKeyDownNoApi}
          color="primary"
          sx={{
            flex: 1,
            borderRadius: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#fdfdf7',
              height: isMobile ? '40px' : 'auto'
            }
          }}
        />
        <FormControlLabel
          sx={{ ml: 2 }}
          control={
            <Switch
              checked={isFahrenheit}
              onChange={onToggleTemperature}
              color="primary"
              sx={{ borderRadius: '12px' }}
            />
          }
          label={isFahrenheit ? '°F' : '°C'}
        />
      </Grid>

      {/* Notes field and Bake button in one row */}
      <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          label="Notes"
          fullWidth
          multiline
          rows={2}
          placeholder="Enter any extra details here... Sorry, domigo can sometimes get confused by very unique inputs!"
          value={notes}
          onChange={(e) => setField('notes', e.target.value)}
          //onKeyDown={handleNotesKeyDown}
          color="primary"
          sx={{
            borderRadius: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#fdfdf7'
            }
          }}
        />
        
        {/* Bake Button */}
        <Box sx={{ ml: 2 }}>
          <Button
            variant="contained"
            disabled={!isValid}
            sx={{
              borderRadius: '20px',
              color: '#ffffff',
              fontSize: '14px',
              padding: '6px 16px',
              marginLeft: '6px',
              marginRight: '13px',
              opacity: isValid ? 1 : 0.5,
              '&:hover': {
                backgroundColor: '#4a2c0d',
              }
            }}
            onClick={() => handleKeyDown({ key: 'Enter', preventDefault: () => {} })} // Simulate an Enter key press
          >
            Bake
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
}
